import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import { useDocumentFormatsState } from '../../hooks/useDocumentFormats';
import { documentOpensIn } from '../../utils/fileFormats';
import { downloadProjectFile } from '../../utils/fileDownload';
import { fitView, zoomAt, zoomLabel, type ImageView } from '../../utils/imageView';
import { decodeTiff, decodedToBlob } from '../../utils/tiffPreview';
import styles from './ImageViewer.module.css';
import Icon from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';
// A TIFF is decoded in the browser, so all of it is held in memory first.
const TIFF_PREVIEW_MAX_BYTES = 300 * 1024 * 1024;
const STEP = 1.25;

const FILE_QUERY = gql`
  query ImageFile($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      extension
      status
    }
  }
`;

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Mounted on /projects/:projectId/viewer-image/:fileId (signed in) and
// /share/:token/viewer-image/:fileId (share link), and embedded in the Scene's
// split pane. Opens the document formats File Formats send to the Image Viewer.
type ImageViewerProps = {
  fileId?: string;
  embedded?: boolean;
};

const ImageViewer = ({ fileId: fileIdProp, embedded = false }: ImageViewerProps) => {
  const { fileId: fileIdParam, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const fileId = fileIdProp ?? fileIdParam;
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const { data, loading: fileLoading } = useQuery(FILE_QUERY, { variables: { id: fileId }, skip: !fileId || isShare });
  const { data: shareData, loading: shareLoading } = useQuery(SHARED_LINK_QUERY, { variables: { token: shareToken }, skip: !isShare });
  const shareLink = shareData?.sharedLink;
  const fileInfo: { id: string; originalName: string; fileSize: number; extension: string } | undefined = isShare
    ? shareLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;
  const canDownload = !isShare || !!shareLink?.allowDownload;

  const { formats, ready: formatsReady } = useDocumentFormatsState();
  const decided = formatsReady && !(isShare ? shareLoading : fileLoading);
  const ext = (fileInfo?.extension || '').toLowerCase();
  const isTiff = ext === 'tif' || ext === 'tiff';
  // Anything File Formats do not send here (a download-only format reached by
  // an old link) is never fetched for viewing: a view counts as an open.
  const notImage = decided && !!fileInfo && documentOpensIn(ext, formats) !== 'image';

  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [tooLarge, setTooLarge] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [scaledFrom, setScaledFrom] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<ImageView>({ scale: 1, x: 0, y: 0 });
  const stageRef = useRef<HTMLDivElement>(null);
  const fitScale = useRef(1);
  const drag = useRef<{ x: number; y: number; view: ImageView } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!fileId || !decided) return;
    if (!fileInfo) { setError('This file could not be found'); return; }
    if (notImage) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    const auth = { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` };
    const load = async () => {
      setError('');
      setTooLarge(false);
      setNatural(null);
      setScaledFrom(null);
      if (isTiff && fileInfo.fileSize > TIFF_PREVIEW_MAX_BYTES) { setTooLarge(true); return; }
      try {
        const res = isShare
          ? await fetch(`/api/share/${shareToken}/files/${fileId}/view-url`)
          : await fetch(`/api/files/${fileId}/url`, { headers: auth });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const { url } = await res.json() as { url: string };
        if (!isTiff) {
          // An <img> cannot send the sign-in header; the app's own routes (local
          // development) take it as ?token=, S3 links are signed already.
          const token = localStorage.getItem(TOKEN_KEY);
          const img = !isShare && token && url.startsWith('/api/') ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
          if (!cancelled) setSrc(img);
          return;
        }
        const bytes = await fetch(url, url.startsWith('/api/') ? { headers: auth } : undefined);
        if (!bytes.ok) throw new Error(`Server returned ${bytes.status}`);
        const decoded = await decodeTiff(await bytes.arrayBuffer());
        const blob = await decodedToBlob(decoded);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        if (decoded.width < decoded.fullWidth) setScaledFrom({ w: decoded.fullWidth, h: decoded.fullHeight });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the image');
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, isShare, shareToken, decided, notImage, isTiff, fileInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fit = useCallback(() => {
    const el = stageRef.current;
    if (!el || !natural) return;
    const v = fitView(el.clientWidth, el.clientHeight, natural.w, natural.h);
    fitScale.current = v.scale;
    setView(v);
  }, [natural]);

  useEffect(() => { fit(); }, [fit]);

  // A window resize keeps the image fitted while it was fitted.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      setView((v) => {
        if (Math.abs(v.scale - fitScale.current) > 1e-6 || !natural) return v;
        const next = fitView(el.clientWidth, el.clientHeight, natural.w, natural.h);
        fitScale.current = next.scale;
        return next;
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [natural]);

  const minScale = () => Math.max(0.01, fitScale.current / 2);
  const zoomCentre = (factor: number) => {
    const el = stageRef.current;
    if (!el) return;
    setView((v) => zoomAt(v, factor, el.clientWidth / 2, el.clientHeight / 2, minScale()));
  };
  const actualSize = () => {
    const el = stageRef.current;
    if (!el || !natural) return;
    setView({ scale: 1, x: (el.clientWidth - natural.w) / 2, y: (el.clientHeight - natural.h) / 2 });
  };

  // The wheel listener must not be passive, or the page scrolls instead.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      setView((v) => zoomAt(v, Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top, minScale()));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [src]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, view };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    setView({ ...d.view, x: d.view.x + e.clientX - d.x, y: d.view.y + e.clientY - d.y });
  };
  const onPointerUp = () => {
    drag.current = null;
    setDragging(false);
  };
  const onDoubleClick = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (view.scale < 1 - 1e-6) setView((v) => zoomAt(v, 1 / v.scale, e.clientX - rect.left, e.clientY - rect.top, minScale()));
    else fit();
  };

  const handleDownload = () => {
    if (!fileId || !fileInfo) return;
    if (isShare) {
      window.location.href = `/api/share/${shareToken}/files/${fileId}/download`;
      return;
    }
    downloadProjectFile(fileInfo);
  };

  const showing = !!src && !error && !tooLarge && !notImage;
  const loading = !notImage && !error && !tooLarge && !natural;

  const zoomControls = (
    <div className={styles.zoomGroup}>
      <button className={styles.iconButton} onClick={() => zoomCentre(1 / STEP)} disabled={!natural} title="Zoom out" aria-label="Zoom out">
        <Icon name="remove" style={{ fontSize: 18 }} />
      </button>
      <span className={styles.zoomLabel} aria-label="Zoom">{natural ? zoomLabel(view.scale) : '—'}</span>
      <button className={styles.iconButton} onClick={() => zoomCentre(STEP)} disabled={!natural} title="Zoom in" aria-label="Zoom in">
        <Icon name="add" style={{ fontSize: 18 }} />
      </button>
      <button className={styles.textButton} onClick={fit} disabled={!natural} title="Fit to the window">Fit</button>
      <button className={styles.textButton} onClick={actualSize} disabled={!natural} title="Actual size">1:1</button>
    </div>
  );

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      {!embedded && (
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <Icon name="arrow_back" style={{ fontSize: 17 }} />
              Back
            </button>
            <div className={styles.divider} />
            <Icon name="image" className={styles.fileIcon} />
            <span className={styles.fileName}>{fileInfo?.originalName ?? 'Loading…'}</span>
            {fileInfo && <span className={styles.meta}>{formatSize(fileInfo.fileSize)}</span>}
            {natural && (
              <span className={styles.meta}>
                {scaledFrom ? `${scaledFrom.w} × ${scaledFrom.h}, shown at ${natural.w} × ${natural.h}` : `${natural.w} × ${natural.h}`}
              </span>
            )}
          </div>
          <div className={styles.headerRight}>
            {showing && zoomControls}
            {canDownload && (
              <button className={styles.actionButton} onClick={handleDownload} disabled={!fileInfo}>
                <Icon name="download" style={{ fontSize: 15 }} />
                Download
              </button>
            )}
          </div>
        </header>
      )}

      <div
        ref={stageRef}
        className={`${styles.stage} ${dragging ? styles.dragging : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        {showing && (
          <img
            className={styles.image}
            src={src}
            alt={fileInfo?.originalName ?? 'Image'}
            draggable={false}
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, visibility: natural ? 'visible' : 'hidden' }}
            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            onError={() => setError('The image could not be shown')}
          />
        )}

        {embedded && showing && (
          <div className={styles.floatingBar} onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
            {zoomControls}
            {canDownload && (
              <button className={styles.iconButton} onClick={handleDownload} title="Download" aria-label="Download">
                <Icon name="download" style={{ fontSize: 18 }} />
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className={styles.stateOverlay}>
            <Icon name="image" style={{ fontSize: 34, color: '#94a3b8' }} />
            <p className={styles.stateTitle}>{isTiff ? 'Preparing the TIFF…' : 'Loading image…'}</p>
          </div>
        )}

        {(notImage || tooLarge || error) && (
          <div className={styles.stateOverlay}>
            <Icon name={error ? 'error' : 'draft'} style={{ fontSize: 34, color: error ? '#dc2626' : '#94a3b8' }} />
            <p className={styles.stateTitle}>{fileInfo?.originalName}</p>
            <p className={styles.stateText}>
              {error ? error
                : tooLarge ? 'This TIFF is too large to show here. Download the file to open it.'
                : `.${ext} files have no viewer here. Download the file to open it.`}
            </p>
            {canDownload && fileInfo && (
              <button className={styles.actionButton} onClick={handleDownload}>
                <Icon name="download" style={{ fontSize: 15 }} />
                Download
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageViewer;
